/**
 * File handling: base64 data URIs, image compression, blob viewing.
 *
 * The whole system moves medical attachments and deposit receipts as base64
 * strings inside JSON (Booking.depositReceipt is TEXT('long'), and prescription
 * results are JSONB), so these helpers are load-bearing, not incidental.
 *
 * Sources: `viewMedicalFile` (shared_ui.js), `compressImage` and
 * `previewSelectedReceipt` (patient_dashboard.js), `processImageFile` (app.js).
 */

/** Ceiling the backend accepts — express is configured with a 50mb JSON limit. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** What the medical-file viewer can render inline. */
export const VIEWABLE_MIME_PREFIXES = ['image/'];
export const VIEWABLE_MIME_TYPES = ['application/pdf'];

/** Split a data URI into its mime type and payload. Returns null if malformed. */
export function parseDataUri(dataUri) {
  if (typeof dataUri !== 'string') return null;
  const match = dataUri.match(/^data:([A-Za-z0-9-+/.]+);base64,(.+)$/);
  if (!match) return null;
  return { mimeType: match[1], base64: match[2] };
}

export const isDataUri = (value) => typeof value === 'string' && value.startsWith('data:');

/** Approximate decoded byte length of a base64 payload, without decoding it. */
export function base64ByteLength(base64) {
  if (!base64) return 0;
  const padding = (base64.match(/=+$/) || [''])[0].length;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/** Byte size of a data URI's payload. */
export function dataUriByteLength(dataUri) {
  const parsed = parseDataUri(dataUri);
  return parsed ? base64ByteLength(parsed.base64) : 0;
}

export const canRenderInline = (mimeType) =>
  VIEWABLE_MIME_TYPES.includes(mimeType) ||
  VIEWABLE_MIME_PREFIXES.some((prefix) => String(mimeType).startsWith(prefix));

/**
 * Data URI -> Blob.
 *
 * Chrome refuses to open a `data:` URI in a new tab, which is why the legacy
 * viewer converted to a Blob first. That constraint still applies.
 */
export function dataUriToBlob(dataUri) {
  const parsed = parseDataUri(dataUri);
  if (!parsed) return null;

  const binary = atob(parsed.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: parsed.mimeType });
}

/** Read a File/Blob as a data URI. */
export function readFileAsDataUri(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read file: ${file?.name || 'unknown'}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale and re-encode an image so a phone photo of a transfer receipt does
 * not blow past the JSON body limit. Aspect ratio is preserved.
 *
 * @param {File|Blob} file
 * @param {{maxWidth?: number, maxHeight?: number, quality?: number, mimeType?: string}} [options]
 * @returns {Promise<string>} A JPEG data URI.
 */
export function compressImage(file, options = {}) {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.7,
    mimeType = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Failed to read image for compression'));
    reader.onload = (event) => {
      const image = new Image();
      image.onerror = () => reject(new Error('Failed to decode image for compression'));
      image.onload = () => {
        let { width, height } = image;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        // JPEG has no transparency: a transparent pixel would come out black.
        if (mimeType === 'image/jpeg') {
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL(mimeType, quality));
      };
      image.src = event.target.result;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Normalise a receipt value before sending it.
 *
 * Some older records stored the receipt as a JSON blob with an `origin` field
 * rather than as a bare string; the backend defends against that too, but
 * cleaning it here keeps the stored data consistent going forward.
 */
export function normalizeReceiptValue(value) {
  if (!value) return null;
  if (typeof value === 'object' && value.origin) return value.origin;
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.origin) return parsed.origin;
    } catch {
      /* not JSON after all — use the string as-is */
    }
  }
  return value;
}

/**
 * Collect the file list a prescription result carries.
 *
 * Two formats coexist in live data: a legacy single `resultUrl` string and the
 * current `results: [{ name, type, data }]` array. Both are surfaced.
 */
export function collectResultFiles(item) {
  const files = [];
  if (item?.resultUrl) files.push({ data: item.resultUrl, name: 'نتيجة سابقة (مرفق 1)' });
  if (Array.isArray(item?.results)) files.push(...item.results);
  return files;
}

/** Human-readable size, e.g. "1.4 MB". */
export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/**
 * Hand a file the browser fetched to the person, under a name (an export, a
 * report). The object URL is released once the download has started.
 */
export function saveBlob(blob, fileName) {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** "سجل الوصول 2026-10-18.xlsx" — the name the server gives the same file. */
export function exportFileName(title, format, now = new Date()) {
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const safe = String(title || 'export').replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 60) || 'export';
  return `${safe} ${day}.${format === 'csv' ? 'csv' : 'xlsx'}`;
}
